/** Page header: title, live status pill, theme switch. */

import { engine$, isRunning } from "../engine/scheduler.ts";
import { patchSettings, settings$ } from "../state/store.ts";
import { el, on } from "./dom.ts";

type Theme = "auto" | "light" | "dark";

export function applyTheme(theme: Theme): void {
  const resolved =
    theme === "auto"
      ? window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light"
      : theme;
  document.documentElement.setAttribute("data-bs-theme", resolved);
}

export function createHeader(): HTMLElement {
  const pill = el("span", { class: "badge rounded-pill d-inline-flex align-items-center gap-2" });
  const dot = el("span", { class: "status-dot" });

  const themeSelect = el("select", {
    class: "form-select form-select-sm w-auto",
    "aria-label": "Theme",
  }) as HTMLSelectElement;

  for (const [value, label] of [
    ["auto", "Auto"],
    ["light", "Light"],
    ["dark", "Dark"],
  ] as const) {
    themeSelect.appendChild(el("option", { value, text: label }));
  }

  themeSelect.value = settings$.get().theme;
  on(themeSelect, "change", () => {
    const theme = themeSelect.value as Theme;
    patchSettings({ theme });
    applyTheme(theme);
  });

  // Follow the OS while the user is on "auto".
  window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => {
    if (settings$.get().theme === "auto") applyTheme("auto");
  });

  function syncPill(): void {
    const state = engine$.get();
    const running = isRunning();
    const starving = running && state.status === "starving";

    dot.style.background = running
      ? starving
        ? "var(--deal-orange)"
        : "var(--deal-green)"
      : "var(--bs-secondary-color)";

    pill.className = `badge rounded-pill d-inline-flex align-items-center gap-2 ${
      running ? (starving ? "text-bg-warning" : "text-bg-success") : "text-bg-secondary"
    }`;

    pill.textContent = "";
    pill.appendChild(dot);
    pill.appendChild(document.createTextNode(running ? (starving ? "throttled" : "running") : "stopped"));
  }

  engine$.subscribe(syncPill, false);
  window.setInterval(syncPill, 1000);
  syncPill();

  return el(
    "header",
    { class: "d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4" },
    el(
      "div",
      { class: "d-flex align-items-center gap-3" },
      el("span", { class: "fs-3", "aria-hidden": "true", text: "🎯" }),
      el(
        "div",
        {},
        el("h1", { class: "h4 fw-bold mb-0", text: "Torn Deal Sniper" }),
        el("p", {
          class: "mb-0 text-body-secondary small",
          text: "Watches the item market and bazaars — alerts the second a listing drops below your profit margin.",
        }),
      ),
    ),
    el("div", { class: "d-flex align-items-center gap-2" }, themeSelect, pill),
  );
}
